import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faPhone, faCamera, faMapMarkerAlt, faCalendarAlt, faSpinner, faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import apiClient from "../../../../../services/apiClient";
import apiServices from "../../../../../services/apiService";
import storageService from "../../../../../utils/storageService";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import CommonTable from "../../../../common/table/ComonTable";

const POST_VISITOR = "/Visitor/postVisitor";
const UNIT_URL = "/UnitMaster/GetAllUnitMaster";
const LIST_PATH = "/zentcomm/workforce-management/vendor";
const STAFF_PATH = "/zentcomm/workforce-management/staff";

const fieldClass = "w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all border-gray-200 dark:border-bordercolor";
const isPartTime = (value) => String(value || "").toLowerCase().replace(/[\s_-]/g, "").includes("parttime");

const VendorPass = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useSelector((state) => state.auth);
    const roleId = Number(storageService.getItem("roleId") || user?.roleId || 0);
    const personTypeId = Number(storageService.getItem("personTypeId") || user?.personTypeId || 0);
    const isVendorLogin = roleId === 7 || personTypeId === 4;
    const loggedInVendorId = user?.id || user?.Id || user?.userId || storageService.getItem("employeeCode");
    const vendorId = searchParams.get("vendorId") || (isVendorLogin ? loggedInVendorId : "");
    const [vendorName, setVendorName] = useState(searchParams.get("vendorName") || "");
    const siteId = user?.siteId || 2;

    const [staff, setStaff] = useState([]);
    const [units, setUnits] = useState([]);
    const [selectedUnits, setSelectedUnits] = useState([]);
    const [passes, setPasses] = useState([]);
    const [vendorType, setVendorType] = useState("");
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [form, setForm] = useState({
        staffId: "",
        visitorName: "",
        mobileNumber: "",
        image: "",
        purposeOfVisit: "",
        fromDate: "",
        toDate: ""
    });

    useEffect(() => {
        if (!vendorId) {
            setPageLoading(false);
            return;
        }
        const load = async () => {
            const vendorRes = await apiClient.get("/vendor-management/vendor/GetVendors", {
                params: { siteId, vendorId, pageNumber: 1, pageSize: 5 }
            });
            const vendor = vendorRes.data?.data?.[0];
            if (vendor?.vendorName) setVendorName(vendor.vendorName);
            setVendorType(vendor?.vendorType || "");
            const contracts = vendor?.contracts || [];
            const unitContracts = contracts.filter(item => item.unitId);
            let unitRows = unitContracts.map(item => ({
                id: item.unitId,
                name: item.unitName || `Unit ${item.unitId}`
            }));
            if (unitRows.length === 0 && contracts.some(item => !item.unitId)) {
                const unitRes = await apiServices({
                    method: "GET",
                    url: `${UNIT_URL}?siteId=${siteId}&pageNumber=1&pageSize=1000`
                });
                const rows = unitRes?.data?.data || unitRes?.data || [];
                unitRows = (Array.isArray(rows) ? rows : []).map(unit => ({
                    id: unit.id || unit.unitId || unit.UnitId,
                    name: unit.unitName || unit.UnitName || unit.unitNumber || unit.id
                }));
            }
            setUnits(unitRows);

            const staffRes = await apiClient.get("/staff-management/staff/GetStaff", {
                params: { siteId, vendorId, pageNumber: 1, pageSize: 200 }
            });
            setStaff(staffRes.data?.data || []);

            const passRes = await apiClient.get("/vendor-management/vendor/GetVendorVisits", {
                params: { vendorId, siteId }
            });
            setPasses(passRes.data?.data || []);
        };
        load().catch(() => toast.error("Failed to load vendor pass details.")).finally(() => setPageLoading(false));
    }, [vendorId, siteId]);

    const selectStaff = (staffId) => {
        const person = staff.find(item => String(item.staffId) === String(staffId));
        setForm(prev => ({
            ...prev,
            staffId,
            visitorName: person ? `${person.firstName || ""} ${person.lastName || ""}`.trim() : "",
            mobileNumber: person?.mobileNumber || "",
            image: person?.photoUrl || ""
        }));
    };

    const onImage = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => setForm(prev => ({ ...prev, image: reader.result }));
        reader.readAsDataURL(file);
    };

    const toggleUnit = (id) => {
        setSelectedUnits(prev => prev.some(item => String(item) === String(id))
            ? prev.filter(item => String(item) !== String(id))
            : [...prev, id]);
    };

    const imageSrc = form.image
        ? (form.image.startsWith("data:") || form.image.startsWith("http") ? form.image : `data:image/jpeg;base64,${form.image}`)
        : "";

    const submit = async (e) => {
        e.preventDefault();
        if (!isPartTime(vendorType)) {
            toast.error("Generate pass is only for a Part-Time vendor.");
            return;
        }
        if (!form.staffId || !form.fromDate || !form.toDate || selectedUnits.length === 0) {
            toast.error("Select a staff person, dates, and at least one unit.");
            return;
        }
        let image = form.image || null;
        if (image && image.includes(",")) image = image.split(",")[1];
        setLoading(true);
        try {
            const response = await apiServices({
                method: "POST",
                url: POST_VISITOR,
                data: {
                    siteId: Number(siteId),
                    visitorName: form.visitorName,
                    mobileNumber: form.mobileNumber,
                    image,
                    address: vendorName,
                    purposeOfVisit: form.purposeOfVisit,
                    expectedEntryTime: `${form.fromDate}T00:00:00`,
                    expectedExitTime: `${form.toDate}T23:59:59`,
                    unitIds: selectedUnits.map(id => Number(id)),
                    isActive: 1,
                    vendorVisit: true,
                    vendorId: Number(vendorId),
                    staffId: Number(form.staffId)
                }
            });
            if (response?.success || response?.isSuccess || response?.data?.isSuccess) {
                toast.success("Pass created.");
                setForm({ staffId: "", visitorName: "", mobileNumber: "", image: "", purposeOfVisit: "", fromDate: "", toDate: "" });
                setSelectedUnits([]);
                const passRes = await apiClient.get("/vendor-management/vendor/GetVendorVisits", {
                    params: { vendorId, siteId }
                });
                setPasses(passRes.data?.data || []);
            } else {
                toast.error(response?.message || response?.data?.message || "Failed to create the pass.");
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to create the pass.");
        } finally {
            setLoading(false);
        }
    };

    const goBack = () => navigate(isVendorLogin ? STAFF_PATH : LIST_PATH);
    const passRows = passes.map(item => ({
        ...item,
        id: item.vendorVisitorId,
        unitLabel: item.unitName || "Site",
        fromLabel: item.fromDate ? String(item.fromDate).split("T")[0] : "—",
        toLabel: item.toDate ? String(item.toDate).split("T")[0] : "—"
    }));

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 pb-12 min-h-0">
            <Breadcrumbs items={["Workforce Management", "Vendor", "Generate pass"]} />
            <div className="mb-6 mt-4">
                <PageHeadingSection
                    Navbar={true}
                    mainHeading="Generate pass"
                    subHeading={vendorName ? `Create a visit pass for ${vendorName} staff.` : "Create a visit pass for vendor staff."}
                />
            </div>

            <div className="bg-white dark:bg-cardbg rounded-2xl border border-gray-100 dark:border-bordercolor shadow-sm flex flex-col">
                <form onSubmit={submit} className="p-6 md:p-8 flex flex-col">
                    <div className="flex flex-col gap-8 max-w-3xl">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">Staff <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faUser} className="text-gray-400 text-xs" />
                                    </div>
                                    <select value={form.staffId} onChange={(e) => selectStaff(e.target.value)} className={fieldClass}>
                                        <option value="">Select staff</option>
                                        {staff.map(person => (
                                            <option key={person.staffId} value={person.staffId}>
                                                {`${person.firstName || ""} ${person.lastName || ""}`.trim()}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">Name</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faUser} className="text-gray-400 text-xs" />
                                    </div>
                                    <input readOnly value={form.visitorName} className={`${fieldClass} bg-gray-50 dark:bg-transparent`} />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">Mobile</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faPhone} className="text-gray-400 text-xs" />
                                    </div>
                                    <input readOnly value={form.mobileNumber} className={`${fieldClass} bg-gray-50 dark:bg-transparent`} />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">Coming from</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400 text-xs" />
                                    </div>
                                    <input readOnly value={vendorName} className={`${fieldClass} bg-gray-50 dark:bg-transparent`} />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">Purpose of visit</label>
                                <input
                                    value={form.purposeOfVisit}
                                    onChange={(e) => setForm(prev => ({ ...prev, purposeOfVisit: e.target.value }))}
                                    placeholder="Purpose of visit"
                                    className="w-full px-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all border-gray-200 dark:border-bordercolor"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">From <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 text-xs" />
                                    </div>
                                    <input type="date" required value={form.fromDate} onChange={(e) => setForm(prev => ({ ...prev, fromDate: e.target.value }))} className={fieldClass} />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">To <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 text-xs" />
                                    </div>
                                    <input type="date" required value={form.toDate} onChange={(e) => setForm(prev => ({ ...prev, toDate: e.target.value }))} className={fieldClass} />
                                </div>
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">Image</label>
                                <div className="flex items-center gap-4">
                                    <div className="w-16 h-16 rounded-lg border border-gray-200 dark:border-bordercolor bg-gray-50 dark:bg-transparent overflow-hidden flex items-center justify-center">
                                        {imageSrc ? <img alt="" src={imageSrc} className="w-full h-full object-cover" /> : <FontAwesomeIcon icon={faCamera} className="text-gray-300" />}
                                    </div>
                                    <label className="px-3 py-2 border border-dashed border-gray-200 dark:border-bordercolor rounded-md text-xs font-semibold text-gray-600 dark:text-textsecondary cursor-pointer hover:border-blue-400">
                                        Change image
                                        <input type="file" accept="image/*" onChange={onImage} className="hidden" />
                                    </label>
                                </div>
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-2">Units <span className="text-red-500">*</span></label>
                                {units.length === 0 && !pageLoading && <p className="text-xs text-gray-500">No units are available for this vendor.</p>}
                                <div className="flex flex-wrap gap-2">
                                    {units.map(unit => {
                                        const checked = selectedUnits.some(id => String(id) === String(unit.id));
                                        return (
                                            <label key={unit.id} className={`text-xs border rounded-md px-3 py-2 cursor-pointer ${checked ? "border-blue-400 bg-blue-50 text-blue-700" : "border-gray-200 dark:border-bordercolor text-gray-700 dark:text-textsecondary"}`}>
                                                <input type="checkbox" className="mr-2" checked={checked} onChange={() => toggleUnit(unit.id)} />
                                                {unit.name}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-between mt-10 border-t border-gray-100 dark:border-bordercolor pt-4 shrink-0 max-w-3xl">
                        <button type="button" onClick={goBack} className="px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-xs flex items-center gap-2 text-gray-600 dark:text-textsecondary hover:bg-gray-50 transition-colors">
                            <FontAwesomeIcon icon={faAngleLeft} />
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || pageLoading}
                            className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                            style={{ backgroundColor: "var(--color-secondaryaccent)", color: "var(--color-primaryaccent)" }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "var(--color-primaryaccent)";
                                e.currentTarget.style.color = "#FFFFFF";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "var(--color-secondaryaccent)";
                                e.currentTarget.style.color = "var(--color-primaryaccent)";
                            }}
                        >
                            {loading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : "Create pass"}
                            <FontAwesomeIcon icon={faAngleRight} />
                        </button>
                    </div>
                </form>
            </div>

            <div className="mt-6">
                <CommonTable
                    loading={pageLoading}
                    tableHeading="Passes"
                    headers={[
                        { label: "Visit", key: "visitingId" },
                        { label: "Unit", key: "unitLabel" },
                        { label: "Purpose", key: "purposeOfVisit" },
                        { label: "From", key: "fromLabel" },
                        { label: "To", key: "toLabel" }
                    ]}
                    data={passRows}
                    renderCell={(key, row) => <span className="text-sm font-medium text-gray-800 dark:text-textsecondary">{row[key] || "—"}</span>}
                />
            </div>
        </div>
    );
};

export default VendorPass;
